import React, { useState, useEffect } from 'react';

import Accordion from 'b2b-ui-components-lib/lib/components/accordion';
import Input from 'b2b-ui-components-lib/lib/components/input';
import Datepicker from 'b2b-ui-components-lib/lib/components/datePicker';
import Checkbox from 'b2b-ui-components-lib/lib/components/checkbox';

import FileUpload from 'home/view/manage-feasibility/pages/DedicatedOrderSummary/FileUpload';
import { formSchema, getDefaultValue, getDefaultCheckBoxValue, getDefaultFiles, ignoreKeys, purchaseOrderExpiryDateKey } from './FormControl';

import { isFormValid } from 'common/utils/validator';
import { DOCUMENT_TYPE_ENUM } from 'home/view/manage-feasibility/constants';
import { MULTI_SITE_MODULE_ID, SUMMARY_AUTH_TOKEN, ERROR_TOAST, DEFAULT_TOAST_MSG, SHORT_CB_DELAY } from 'home/view/manage-feasibility/constants';
import { downloadSummaryErrorSheet, deleteDocumentsForSummary } from 'home/view/manage-feasibility/services';
import { getFromSessionStorage, getPreviousYearDate, getRange } from 'common/utils/app.utils';
import { getFileName, getFileType } from 'home/view/manage-feasibility/utils';
import { PO_MAX_YEAR_BACK } from 'common/constants/app.constants';

const PurchaseDetails = ({ order = {}, updateParentForm, setFormStatus, updateOrder = () => {}, setToast = () => {}, isOrderConfirmation = false }) => {
  const fileSchema = {
    supportedTypes: ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'],
    maxFileLimit: 1024 * 1024,
    documentNames: ['PO_DOCUMENT', 'PO_DOCUMENT_2', 'PO_DOCUMENT_3'],
    documentType: DOCUMENT_TYPE_ENUM.PURCHASE_ORDER,
    allowMultiplePDFs: true
  };

  const defaultFormState = getDefaultValue(order);

  const [form, setForm] = useState(defaultFormState);
  const [accordionMetadata, setAccordionMetadata] = useState({
    isValidated: false,
    isExpanded: true
  });
  const [defaultFileArr, setDefaultFileArr] = useState([]);
  const [checked, setChecked] = useState(true);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  useEffect(() => {
    setDefaultFileArr(getDefaultFiles(order.documents));
  }, [order]);

  const onCheckBoxChange = (value) => {
    setChecked(value);
    setForm(formSchema);
  };

  const onPurchaseOrderNo = (value) => {
    setForm({
      ...form,
      purchaseOrderNo: {
        ...form.purchaseOrderNo,
        value,
        touched: true,
        errors: form.purchaseOrderNo.validator(value)
      }
    });
  };

  const onPurchaseOrderDateChange = (value, keyName) => {
    if (value) {
      setForm({
        ...form,
        [keyName]: { ...form[keyName], value, touched: true, errors: [] }
      });
      return;
    }
    setForm({
      ...form,
      [keyName]: {
        ...form[keyName],
        value,
        touched: true,
        errors: keyName === purchaseOrderExpiryDateKey ? [] : ['This field is mandatory']
      }
    });
  };

  const onPurchaseOrderTotalCharges = (value) =>
    setForm({
      ...form,
      purchaseOrderTotalCharges: {
        ...form.purchaseOrderTotalCharges,
        value,
        touched: true,
        errors: form.purchaseOrderTotalCharges.validator(value)
      }
    });

  const onFileUpload = (files, type, uploadedFiles) => {
    setForm({ ...form, files });
    if (files) {
      setUploadedFiles(uploadedFiles);
    } else {
      setUploadedFiles([]);
    }
  };

  const onFieldBlur = (formElement, keyName) => {
    const { value, validator, errors: existingError } = formElement;
    let errors = [];
    if (typeof validator === 'function') {
      errors = validator(value);
    }
    setForm({
      ...form,
      [keyName]: {
        ...form[keyName],
        value,
        errors: errors?.concat(existingError),
        touched: true
      }
    });
  };

  useEffect(() => {
    const poAmount = form && form.purchaseOrderTotalCharges && form.purchaseOrderTotalCharges.value;
    const poNumber = form && form.purchaseOrderNo && form.purchaseOrderNo.value;
    const purchaseOrderDate = form && form.purchaseOrderDate && form.purchaseOrderDate.value;
    const fileUrl = (form && form.files.value) || (form && form.files === 'success');

    const isValid = !!(poAmount && poNumber && purchaseOrderDate && fileUrl);

    if (isFormValid(form, ignoreKeys)) {
      setAccordionMetadata({ isValidated: true, isExpanded: true });
      updateParentForm({ purchaseDetail: form });
      setFormStatus({ purchaseDetail: true });
    } else if (isValid) {
      setAccordionMetadata({
        isValidated: isValid,
        isExpanded: isValid
      });
      updateParentForm({ purchaseDetail: form });
      setFormStatus({ purchaseDetail: true });
    } else {
      const hasValue = !!(poAmount || poNumber || purchaseOrderDate || fileUrl);

      setAccordionMetadata({
        isValidated: false,
        isExpanded: hasValue
      });
      updateParentForm({ purchaseDetail: null });
      setChecked(checked);
      setFormStatus({ purchaseDetail: !checked ? true : false });
    }

    let timeoutId;
    if (checked && isFormValid(form, ignoreKeys)) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => updateOrder(form, true, true), SHORT_CB_DELAY);
    } else if (!checked) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        updateOrder(form, true, false);
        deleteUploadedFile();
      }, SHORT_CB_DELAY);
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [form, checked]);

  const deleteUploadedFile = () => {
    const summaryAuthToken = getFromSessionStorage(MULTI_SITE_MODULE_ID, SUMMARY_AUTH_TOKEN);

    Array.isArray(uploadedFiles) &&
      uploadedFiles.forEach(async (_, index) => {
        const documentName = fileSchema.documentNames[index];
        const payload = { documentType: fileSchema.documentType, documentName };

        try {
          const { res, err } = await deleteDocumentsForSummary(payload, summaryAuthToken);

          if (res) {
            setUploadedFiles([]);
            setDefaultFileArr([]); // clear default files also after checkbox unchecked in PO details form
          } else if (err) {
            const errorMsg = (err && err.response && err.response.data && err.response.data.errorMsg) || DEFAULT_TOAST_MSG;

            setToast(true, errorMsg, ERROR_TOAST);

            throw new Error(errorMsg);
          }
        } catch (e) {
          console.error(e);
        }
      });
  };

  return (
    <React.Fragment>
      <Accordion
        title="Purchase Order Details"
        autoScrollOnExpand={false}
        isExpanded={true}
        isValidated={accordionMetadata.isValidated}
        isEditable={accordionMetadata.isEditable}
        onEditClick={() =>
          setAccordionMetadata(({ isEditable }) => ({
            ...accordionMetadata,
            isEditable: !isEditable
          }))
        }
      >
        {/* <div className="accordion_body">
          <Checkbox checked={checked} onChange={onCheckBoxChange} disabled={true}>
            <span className="check_text">Add PO details in my bill</span>
          </Checkbox>
          {checked && (
            <div className="order_details_form">
              <div className="row field_row">
                <div className="flg-lg-12 flg-md-12">
                  <div className="form_area">
                    <div className="row">
                      <div className="flg-lg-6 flg-md-6">
                        <Input
                          label="Purchase Order Number"
                          required
                          onBlur={() => onFieldBlur(form.purchaseOrderNo, 'purchaseOrderNo')}
                          errors={form.purchaseOrderNo.errors}
                          touched={form.purchaseOrderNo.touched}
                          value={form.purchaseOrderNo.value}
                          onChange={onPurchaseOrderNo}
                          disabled={isOrderConfirmation}
                        />
                      </div>

                      <div className="flg-lg-6 flg-md-6">
                        <Datepicker
                          name="datepicker"
                          label="Purchase Order Date"
                          required={true}
                          errors={form.purchaseOrderDate.errors}
                          touched={form.purchaseOrderDate.touched}
                          value={form.purchaseOrderDate.value}
                          onChange={(value) => onPurchaseOrderDateChange(value, 'purchaseOrderDate')}
                          maxDate={new Date()}
                          minDate={getPreviousYearDate(PO_MAX_YEAR_BACK)}
                          years={getRange(getPreviousYearDate(PO_MAX_YEAR_BACK).getFullYear(), new Date().getFullYear())}
                          disabled={isOrderConfirmation}
                        />
                      </div>

                      <div className="flg-lg-6 flg-md-6">
                        <Input
                          label="Purchase Order Total Charges"
                          required
                          onBlur={() => onFieldBlur(form.purchaseOrderTotalCharges, 'purchaseOrderTotalCharges')}
                          errors={form.purchaseOrderTotalCharges.errors}
                          touched={form.purchaseOrderTotalCharges.touched}
                          value={form.purchaseOrderTotalCharges.value}
                          onChange={onPurchaseOrderTotalCharges}
                          disabled={isOrderConfirmation}
                        />
                      </div>

                      <div className="flg-lg-6 flg-md-6">
                        <Datepicker
                          name="datepicker"
                          label="Purchase Order Expiry Date (Optional)"
                          required={true}
                          errors={form.purchaseOrderExpiryDate.errors}
                          touched={form.purchaseOrderExpiryDate.touched}
                          value={form.purchaseOrderExpiryDate.value}
                          onChange={(value) => onPurchaseOrderDateChange(value, 'purchaseOrderExpiryDate')}
                          maxDate={new Date(new Date().setYear(new Date().getFullYear() + 5))}
                          minDate={new Date()}
                          years={getRange(new Date().getFullYear(), new Date().getFullYear() + 5)}
                          disabled={isOrderConfirmation}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        {checked && ( */}
        <div className="accordion_footer aoi_po">
          <FileUpload
            onFileUploadSuccess={onFileUpload}
            label="Upload Purchase Order Document"
            supportedLabel="Supported file format is .PDF, .PNG, .JPG"
            limitLabel="Maximum file size is 1 Mb"
            fileSchema={fileSchema}
            defaultFiles={defaultFileArr}
            setToast={setToast}
            isOrderConfirmation={isOrderConfirmation}
            multiple={true}
          ></FileUpload>
        </div>
        {/* )} */}
      </Accordion>
    </React.Fragment>
  );
};

export default PurchaseDetails;
